import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Store,
  Trash2,
  RefreshCw,
  Tag,
  Calendar,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminMerchantDiscounts({ user }) {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, discount: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/merchant-discounts`, {
        withCredentials: true,
      });
      setDiscounts(response.data || []);
    } catch (error) {
      console.error("Error fetching discounts:", error);
      toast.error("Failed to load merchant discounts");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.discount) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/merchant-discounts/${deleteDialog.discount.id}`, {
        withCredentials: true,
      });
      toast.success("Discount deleted successfully");
      setDeleteDialog({ open: false, discount: null });
      fetchDiscounts();
    } catch (error) {
      console.error("Error deleting discount:", error);
      toast.error("Failed to delete discount");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "No expiry";
    return new Date(dateStr).toLocaleDateString("en-SG", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isExpired = (discount) => {
    if (!discount.valid_until) return false;
    return new Date(discount.valid_until) < new Date();
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Store className="h-6 w-6 text-amber-400" />
                Merchant Discounts
              </h1>
              <p className="text-slate-400 text-sm">
                Manage discounts created by merchants
              </p>
            </div>
          </div>
          <Button onClick={fetchDiscounts} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-white">{discounts.length}</div>
            <div className="text-sm text-slate-400">Total Discounts</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-emerald-400">
              {discounts.filter((d) => d.is_active && !isExpired(d)).length}
            </div>
            <div className="text-sm text-slate-400">Active</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-orange-400">
              {discounts.filter((d) => isExpired(d)).length}
            </div>
            <div className="text-sm text-slate-400">Expired</div>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700 p-4">
            <div className="text-2xl font-bold text-red-400">
              {discounts.filter((d) => !d.is_active).length}
            </div>
            <div className="text-sm text-slate-400">Inactive</div>
          </Card>
        </div>

        {/* Discounts Table */}
        <Card className="bg-slate-800/50 border-slate-700">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : discounts.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Store className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No merchant discounts found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Title</TableHead>
                  <TableHead className="text-slate-400">Merchant</TableHead>
                  <TableHead className="text-slate-400">Code</TableHead>
                  <TableHead className="text-slate-400">Discount</TableHead>
                  <TableHead className="text-slate-400">Expires</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discounts.map((discount) => (
                  <TableRow key={discount.id} className="border-slate-700">
                    <TableCell>
                      <div className="font-medium text-white">{discount.title}</div>
                      {discount.description && (
                        <p className="text-xs text-slate-400 mt-1 max-w-[200px] truncate">
                          {discount.description}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-300">{discount.merchant_name || "Unknown"}</span>
                    </TableCell>
                    <TableCell>
                      <code className="bg-slate-900 px-2 py-1 rounded font-mono text-amber-400 text-sm">
                        {discount.discount_code}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-emerald-500/20 text-emerald-400">
                        {discount.discount_percentage}% OFF
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-slate-400" />
                        <span className={isExpired(discount) ? "text-red-400" : ""}>
                          {formatDate(discount.valid_until)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {!discount.is_active ? (
                        <Badge className="bg-red-500/20 text-red-400">Inactive</Badge>
                      ) : isExpired(discount) ? (
                        <Badge className="bg-orange-500/20 text-orange-400">Expired</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, discount })}
                        className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-discount-${discount.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={deleteDialog.open}
          onOpenChange={(open) => setDeleteDialog({ open, discount: open ? deleteDialog.discount : null })}
        >
          <AlertDialogContent className="bg-slate-900 border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Merchant Discount?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                Are you sure you want to permanently delete the discount{" "}
                <span className="text-amber-400 font-semibold">
                  "{deleteDialog.discount?.title}"
                </span>{" "}
                (code: <code className="text-amber-400">{deleteDialog.discount?.discount_code}</code>)?
                <br />
                <br />
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
